import { useEffect, type FC } from 'react';
import { useWatch, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { Button } from '@ui/button.tsx';
import { Input } from '@ui/input.tsx';
import { FormControl, FormField, FormItem, FormMessage } from '@ui/form.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ui/select.tsx';
import type { SegmentCriterionSegmentCriterionReadModel } from '@r/api/generated/client.schemas.ts';
import type { SegmentFormValues } from './segment-form-types.ts';
import { getChoiceLabel, getChoiceValue } from './segment-choice-utils.ts';

interface SegmentRuleRowProps {
  form: UseFormReturn<SegmentFormValues>;
  index: number;
  criteria: SegmentCriterionSegmentCriterionReadModel[];
  onRemove: () => void;
}

/**
 * Affiche une ligne de règle (critère, opérateur, valeur) et sélectionne automatiquement
 * une valeur par défaut lorsque le critère est de type "choice".
 */
export const SegmentRuleRow: FC<SegmentRuleRowProps> = ({ form, index, criteria, onRemove }) => {
  const { t } = useTranslation();
  const selectedField = useWatch({ control: form.control, name: `rules.${index}.field` });
  const selectedValue = useWatch({ control: form.control, name: `rules.${index}.value` });
  const criterion = criteria.find((c) => c.field === selectedField);

  useEffect(() => {
    if (criterion?.type === 'choice' && criterion.choices?.length && !selectedValue) {
      form.setValue(`rules.${index}.value`, getChoiceValue(criterion.choices[0]));
    }
  }, [criterion, selectedValue]);

  const handleFieldChange = (value: string) => {
    const newCriterion = criteria.find((c) => c.field === value);

    form.setValue(`rules.${index}.field`, value);
    form.setValue(`rules.${index}.operator`, newCriterion?.operators[0] ?? '');
    form.setValue(
      `rules.${index}.value`,
      newCriterion?.type === 'choice' && newCriterion.choices?.[0]
        ? getChoiceValue(newCriterion.choices[0])
        : ''
    );
  };

  return (
    <div className="flex items-start gap-2">
      <FormField
        control={form.control}
        name={`rules.${index}.field`}
        render={({ field }) => (
          <FormItem className="flex-1">
            <Select onValueChange={handleFieldChange} value={field.value}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {criteria.map((c) => (
                  <SelectItem key={c.field} value={c.field}>
                    {t(c.label)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name={`rules.${index}.operator`}
        render={({ field }) => (
          <FormItem className="flex-1">
            <Select onValueChange={field.onChange} value={field.value} disabled={!criterion}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {(criterion?.operators ?? []).map((operator) => (
                  <SelectItem key={operator} value={operator}>
                    {t(`admin.segments.operator.${operator}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name={`rules.${index}.value`}
        render={({ field, fieldState }) => (
          <FormItem className="flex-1">
            <FormControl>
              {criterion?.type === 'choice' ? (
                <Select onValueChange={field.onChange} value={field.value} disabled={!criterion}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(criterion.choices ?? []).map((choice) => {
                      const choiceValue = getChoiceValue(choice);

                      return (
                        <SelectItem key={choiceValue} value={choiceValue}>
                          {getChoiceLabel(choice, t)}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  placeholder={t('admin.segments.rules.value.placeholder')}
                  disabled={!criterion}
                  {...field}
                  error={!!fieldState.error}
                />
              )}
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <Button
        type="button"
        variant="ghost"
        className="mt-1 shrink-0"
        onClick={onRemove}
        aria-label={t('admin.segments.rules.remove')}
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );
};
